'use client';

import { Box, Button, Checkbox, HStack, Input, Popover, Portal, Text } from '@chakra-ui/react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useAppRailOverlay } from '@/components/core/app-layout/AppRailContext';
import { useVoicePageControl, voiceSelectOption } from '@/lib/voice/page-controls';
import type { PromptGroup } from '@/types/database-schema';
import { topicOrigin } from '@/utils/topicOptions';
import {
  EMPTY_TOPIC_FILTER,
  parseTopicFilterValue,
  resolvePromptTopicFilter,
  promptTopicFilterNeedsInventory,
  topicFilterBase,
  topicFilterValue,
  type AppliedPromptTopicFilter,
  type PromptTopicFilter,
} from '@/utils/promptTopicFilter';
import {
  FILTER_MENU_CONTENT_PROPS,
  FILTER_MENU_ITEM_PROPS,
  InlineFilterTrigger,
} from './InlineFilterControls';

type PromptOption = { prompt_id: string; text?: string | null };
const EMPTY_PROMPTS: PromptOption[] = [];

interface PromptScopeFilterProps {
  topics: PromptGroup[];
  topicId: string;
  promptId?: string;
  subtopicId?: string;
  prompts?: PromptOption[];
  promptInventoryReady?: boolean;
  customPromptIds?: string[];
  topicFilter?: PromptTopicFilter;
  onChange: (
    topicId: string,
    promptId: string,
    subtopicId?: string,
    filter?: AppliedPromptTopicFilter
  ) => void;
  hideLabel?: boolean;
}

/** One compact popup applies topic unions and exclusions atomically to every report. */
export function PromptScopeFilter({
  topics,
  topicId,
  promptId = '',
  subtopicId = '',
  prompts = EMPTY_PROMPTS,
  promptInventoryReady = false,
  topicFilter,
  customPromptIds,
  onChange,
  hideLabel = true,
}: PromptScopeFilterProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PromptTopicFilter>(EMPTY_TOPIC_FILTER);
  const [mode, setMode] = useState<'include' | 'exclude'>('include');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const onRailOverlayChange = useAppRailOverlay();
  const currentValue = topicFilterValue(topicId, subtopicId, promptId);
  const inventoryIds = useMemo(() => prompts.map(prompt => prompt.prompt_id), [prompts]);
  const result = useMemo(
    () => resolvePromptTopicFilter(draft, topics, inventoryIds),
    [draft, topics, inventoryIds]
  );
  const choices = useMemo(() => {
    const topicByPrompt = new Map<string, PromptGroup>();
    const values = [...topics]
      .sort((a, b) => a.name.localeCompare(b.name))
      .flatMap(topic => {
        topic.prompt_ids.forEach(id => {
          if (!topicByPrompt.has(id)) topicByPrompt.set(id, topic);
        });
        return [
          {
            value: topicFilterValue(topic.id),
            label: topic.name,
            count: topic.prompt_ids.length,
            members: topic.prompt_ids,
            topicId: topic.id,
            child: false,
            origin: topicOrigin(topic),
          },
          ...[...(topic.subtopics || [])]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(child => ({
              value: topicFilterValue(topic.id, child.id),
              label: child.name,
              count: child.prompt_ids.length,
              members: child.prompt_ids,
              topicId: topic.id,
              child: true,
              origin: topicOrigin(topic),
            })),
        ];
      });
    return [
      ...values,
      ...prompts.map(prompt => {
        const topic = topicByPrompt.get(prompt.prompt_id);
        const child = topic?.subtopics?.find(child => child.prompt_ids.includes(prompt.prompt_id));
        return {
          value: topicFilterValue(topic?.id || '', child?.id || '', prompt.prompt_id),
          label: prompt.text || prompt.prompt_id,
          count: 1,
          members: [prompt.prompt_id],
          topicId: topic?.id || '',
          child: true,
          origin: topicOrigin(topic),
          prompt: true,
        };
      }),
    ];
  }, [topics, prompts]);
  const current =
    topicFilter ||
    (customPromptIds?.length && !promptId
      ? {
          include: customPromptIds.map(
            id =>
              choices.find(
                choice => 'prompt' in choice && parseTopicFilterValue(choice.value)?.promptId === id
              )?.value || topicFilterValue('', '', id)
          ),
          exclude: [],
        }
      : currentValue
        ? { include: [currentValue], exclude: [] }
        : EMPTY_TOPIC_FILTER);
  const labelFor = (value: string) =>
    choices.find(choice => choice.value === value)?.label || 'Unavailable selection';
  const selectedTopic = topics.find(topic => topic.id === topicId);
  const selectedChild = selectedTopic?.subtopics?.find(child => child.id === subtopicId);
  const missingChild = Boolean(subtopicId && !selectedChild);
  const activeLabel = topicFilter
    ? `${topicFilter.include.length === 1 ? labelFor(topicFilter.include[0]) : topicFilter.include.length ? `${topicFilter.include.length} selections` : 'All topics'}${topicFilter.exclude.length ? ` · ${topicFilter.exclude.length} excluded` : ''}`
    : topicId
      ? `${selectedTopic?.name || 'Topic'}${subtopicId ? ` / ${selectedChild?.name || 'Subtopic unavailable'}` : ''}`
      : promptId
        ? prompts.find(prompt => prompt.prompt_id === promptId)?.text || promptId
        : customPromptIds?.length
          ? `${customPromptIds.length} prompts`
          : 'All topics';
  const voiceOptions = [{ value: '', label: 'All topics' }, ...choices];
  const voiceRef = useVoicePageControl({
    label: 'Topic',
    kind: 'select',
    value: currentValue,
    options: voiceOptions,
    apply: requested => {
      const next = voiceSelectOption(voiceOptions, requested);
      const ref = parseTopicFilterValue(next);
      onChange(ref?.topicId || '', ref?.promptId || '', ref?.subtopicId || '');
      return next;
    },
  });
  const query = search.trim().toLocaleLowerCase();
  const activeParents = new Set(
    [...draft.include, ...draft.exclude].map(value => parseTopicFilterValue(value)?.topicId)
  );
  const visible = choices
    .filter(choice => {
      if ('prompt' in choice)
        return Boolean(query) && choice.label.toLocaleLowerCase().includes(query);
      if (query)
        return (
          choice.label.toLocaleLowerCase().includes(query) ||
          (!choice.child &&
            choices.some(
              child =>
                child.topicId === choice.topicId && child.label.toLocaleLowerCase().includes(query)
            ))
        );
      return !choice.child || expanded.includes(choice.topicId);
    })
    .sort((a, b) => Number(activeParents.has(b.topicId)) - Number(activeParents.has(a.topicId)));
  const matchingIds = new Set(result.promptIds);
  const covers = (parent: string, value: string) => {
    const left = parseTopicFilterValue(parent);
    const right = parseTopicFilterValue(value);
    if (!left || !right || left.promptId || !left.topicId) return parent === value;
    return (
      left.topicId === right.topicId && (!left.subtopicId || left.subtopicId === right.subtopicId)
    );
  };
  const checkedFor = (choice: (typeof choices)[number]): boolean | 'indeterminate' => {
    if (mode === 'exclude') return draft.exclude.includes(choice.value);
    if (draft.exclude.some(value => covers(value, choice.value))) return false;
    const directlyIncluded = draft.include.some(value => covers(value, choice.value));
    const matches = choice.members.filter(id => matchingIds.has(id)).length;
    if (directlyIncluded)
      return matches === choice.members.length ? true : matches ? 'indeterminate' : false;
    return draft.include.length && matches && !('prompt' in choice) ? 'indeterminate' : false;
  };
  const toggle = (value: string, checked: boolean) =>
    setDraft(previous => {
      if (mode === 'exclude')
        return {
          ...previous,
          exclude: checked
            ? [...new Set([...previous.exclude, value])]
            : previous.exclude.filter(item => item !== value),
        };
      const ancestor = previous.include.some(item => item !== value && covers(item, value));
      const next = {
        include: checked
          ? ancestor
            ? previous.include
            : [...new Set([...previous.include, value])]
          : previous.include.filter(item => !covers(value, item)),
        exclude: checked
          ? previous.exclude.filter(item => !covers(value, item))
          : ancestor
            ? [...new Set([...previous.exclude, value])]
            : previous.exclude,
      };
      const ref = parseTopicFilterValue(value);
      if (checked && ref?.topicId && !ref.subtopicId && !ref.promptId)
        setExpanded(previous => [...new Set([...previous, ref.topicId])]);
      return next;
    });
  const needsInventory =
    (draft.include.length > 1 || draft.exclude.length > 0) &&
    promptTopicFilterNeedsInventory(draft);
  const canApply =
    result.valid &&
    (!needsInventory || promptInventoryReady) &&
    !((draft.include.length > 1 || draft.exclude.length > 0) && !result.promptIds.length);
  const apply = () => {
    if (!canApply) return;
    const base = topicFilterBase(draft);
    if (!draft.include.length && !draft.exclude.length) onChange('', '', '');
    else if (draft.include.length === 1 && !draft.exclude.length && base)
      onChange(base.topicId, base.promptId, base.subtopicId);
    else
      onChange(base?.topicId || '', '', base?.subtopicId || '', {
        ...draft,
        promptIds: result.promptIds,
      });
    setOpen(false);
    onRailOverlayChange({ open: false });
  };
  return (
    <Box ref={voiceRef} display="inline-flex" flexShrink={0} alignItems="center" gap="2">
      <Popover.Root
        lazyMount
        unmountOnExit
        initialFocusEl={() => inputRef.current}
        open={open}
        onOpenChange={({ open: next }) => {
          setOpen(next);
          onRailOverlayChange({
            open:
              next && Boolean(triggerRef.current?.closest('[data-section="railSectionControls"]')),
          });
          if (next) {
            setDraft(current);
            setSearch('');
            setMode('include');
            setExpanded([
              ...new Set(current.include.map(value => parseTopicFilterValue(value)?.topicId || '')),
            ]);
            requestAnimationFrame(() => inputRef.current?.focus());
          }
        }}
        positioning={{
          placement: 'bottom-start',
          strategy: 'fixed',
          gutter: 8,
          overflowPadding: 12,
          slide: true,
          flip: true,
          fitViewport: true,
        }}
      >
        <Popover.Trigger asChild>
          <InlineFilterTrigger
            ref={triggerRef}
            label="Topic"
            activeLabel={activeLabel}
            isFiltered={Boolean(currentValue || topicFilter || customPromptIds?.length)}
            hideLabel={hideLabel}
            hideLabelOnMobile
            minSelectWidth={{ base: '96px', md: '150px' }}
            secondaryLabel={topicId && promptId ? '1 prompt' : undefined}
          />
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner zIndex="popover">
            <Popover.Content
              {...FILTER_MENU_CONTENT_PROPS}
              w="360px"
              maxW="calc(100vw - 24px)"
              p="3"
              maxH="calc(100vh - 48px)"
              overflow="hidden"
              aria-label="Topic filter"
            >
              <HStack justify="space-between" mb="2">
                <Popover.Title fontSize="0.8125rem" fontWeight="600">
                  Topics
                </Popover.Title>
                <Popover.CloseTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    minW="24px"
                    h="24px"
                    p="0"
                    aria-label="Close topic filter"
                  >
                    <X size={14} />
                  </Button>
                </Popover.CloseTrigger>
              </HStack>
              <HStack gap="1" mb="2">
                {(['include', 'exclude'] as const).map(value => (
                  <Button
                    key={value}
                    size="xs"
                    variant="ghost"
                    h="28px"
                    fontSize="0.8125rem"
                    px="2.5"
                    aria-pressed={mode === value}
                    bg={mode === value ? 'ui.surface.input' : undefined}
                    onClick={() => setMode(value)}
                  >
                    {value === 'include' ? 'Include' : 'Exclude'}
                  </Button>
                ))}
              </HStack>
              <Input
                ref={inputRef}
                aria-label="Search topics, subtopics and prompts"
                placeholder="Search topics, subtopics and prompts…"
                value={search}
                onChange={event => setSearch(event.target.value)}
                h={FILTER_MENU_ITEM_PROPS.minH}
                fontSize={FILTER_MENU_ITEM_PROPS.fontSize}
                lineHeight="1.5"
                px="2.5"
                mb="2"
                bg="ui.surface.input"
                borderRadius="8px"
                borderColor="ui.border.subtle"
                onKeyDown={event => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    event.currentTarget
                      .closest('[role="dialog"]')
                      ?.querySelector<HTMLInputElement>(
                        '[data-topic-choices] input[type="checkbox"]'
                      )
                      ?.focus();
                  }
                }}
              />
              {draft.include.length || draft.exclude.length ? (
                <Box mb="2" color="ui.text.secondary" fontSize="xs">
                  {draft.include.length ? (
                    <Text lineClamp={2}>Include: {draft.include.map(labelFor).join(', ')}</Text>
                  ) : (
                    <Text>Include: All topics</Text>
                  )}
                  {draft.exclude.length ? (
                    <Text lineClamp={2}>Exclude: {draft.exclude.map(labelFor).join(', ')}</Text>
                  ) : null}
                </Box>
              ) : null}
              <Box data-topic-choices maxH="280px" overflowY="auto">
                {mode === 'include' && !query ? (
                  <Checkbox.Root
                    checked={!draft.include.length}
                    onCheckedChange={() => setDraft(previous => ({ ...previous, include: [] }))}
                    minH="32px"
                    px="2.5"
                    gap="2"
                    w="full"
                    size="sm"
                  >
                    <Checkbox.HiddenInput aria-label="Include all topics" />
                    <Checkbox.Control />
                    <Checkbox.Label fontSize="0.8125rem">All topics</Checkbox.Label>
                  </Checkbox.Root>
                ) : null}
                {visible.map((choice, index) => (
                  <Box key={choice.value}>
                    {!choice.child &&
                    (index === 0 || visible[index - 1].origin !== choice.origin) ? (
                      <Text px="2.5" pt="2" pb="1" fontSize="xs" color="ui.text.tertiary">
                        {choice.origin === 'shared' ? 'Attune topics' : 'Your topics'}
                      </Text>
                    ) : null}
                    <HStack
                      gap="0"
                      minH="32px"
                      borderRadius="8px"
                      _hover={{ bg: 'ui.surface.input' }}
                    >
                      <Checkbox.Root
                        checked={checkedFor(choice)}
                        onCheckedChange={({ checked }) => toggle(choice.value, checked === true)}
                        flex="1"
                        minW="0"
                        minH="32px"
                        pl={choice.child ? '6' : '2.5'}
                        pr="2"
                        gap="2"
                        size="sm"
                        title={choice.label}
                      >
                        <Checkbox.HiddenInput
                          aria-label={`${mode === 'include' ? 'Include' : 'Exclude'} ${choice.label}`}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggle(choice.value, checkedFor(choice) !== true);
                            }
                          }}
                          data-topic-value={choice.value}
                        />
                        <Checkbox.Control flexShrink={0} />
                        <Checkbox.Label fontSize="0.8125rem" lineHeight="1.5" lineClamp={2}>
                          {choice.label}
                        </Checkbox.Label>
                      </Checkbox.Root>
                      <Text fontSize="xs" color="ui.text.tertiary" px="1">
                        {choice.count}
                      </Text>
                      {!choice.child &&
                      topics.find(topic => topic.id === choice.topicId)?.subtopics?.length ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          h="28px"
                          minW="28px"
                          p="0"
                          aria-label={`Show subtopics in ${choice.label}`}
                          aria-expanded={expanded.includes(choice.topicId)}
                          onClick={() =>
                            setExpanded(previous =>
                              previous.includes(choice.topicId)
                                ? previous.filter(id => id !== choice.topicId)
                                : [...previous, choice.topicId]
                            )
                          }
                        >
                          {expanded.includes(choice.topicId) ? (
                            <ChevronDown size={13} />
                          ) : (
                            <ChevronRight size={13} />
                          )}
                        </Button>
                      ) : (
                        <Box w="2" />
                      )}
                    </HStack>
                  </Box>
                ))}
                {!visible.length ? (
                  <Text px="2.5" py="2" fontSize="0.8125rem" color="ui.text.tertiary">
                    No matches
                  </Text>
                ) : null}
              </Box>
              <HStack
                justify="space-between"
                mt="3"
                pt="2"
                borderTopWidth="1px"
                borderColor="ui.border.subtle"
              >
                <Text fontSize="xs" color="ui.text.tertiary" role="status">
                  {needsInventory && !promptInventoryReady
                    ? 'Prompt list not ready'
                    : result.valid
                      ? `${result.promptIds.length} prompts`
                      : 'Selection unavailable'}
                </Text>
                <HStack gap="1">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      onChange('', '', '');
                      setOpen(false);
                      onRailOverlayChange({ open: false });
                    }}
                  >
                    Clear
                  </Button>
                  <Button size="xs" disabled={!canApply} onClick={apply}>
                    Apply
                  </Button>
                </HStack>
              </HStack>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      {missingChild ? (
        <Text role="status" fontSize="xs" color="ui.text.secondary">
          This subtopic is unavailable. Select another subtopic or the whole topic.
        </Text>
      ) : null}
    </Box>
  );
}
